import { Box, Button, Card, CardContent, Chip, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { ReactNode } from 'react';

import { METRICS } from '../constants';
import { KpiSeriesKey, MetricKey, SnapshotComparison } from '../types';
import {
  coverageColor,
  formatDate,
  formatTrendDelta,
  isPositiveTrend,
  ratingColor,
  ratingLabel,
  toNumber,
} from '../utils';

const quietCardStyle = {
  borderRadius: 16,
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
};

const getDeltaTolerance = (seriesKey: KpiSeriesKey) => {
  if (seriesKey === 'coverage') return 0.05;
  if (seriesKey === 'repo_count' || seriesKey === 'code_smells') return 0.5;
  return 0.005;
};

const getMetricDeltaTolerance = (metric: MetricKey) => {
  if (metric === 'coverage') return 0.05;
  if (metric === 'code_smells') return 0.5;
  return 0.05;
};

export const ButtonPill = ({
  active,
  label,
  onClick,
  disabled = false,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    variant={active ? 'contained' : 'outlined'}
    color={active ? 'primary' : 'default'}
    size="small"
    style={{
      borderRadius: 999,
      textTransform: 'none',
      fontWeight: 800,
      boxShadow: active && !disabled ? '0 8px 18px rgba(37, 99, 235, 0.2)' : 'none',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {label}
  </Button>
);

export const KpiTrendLine = ({
  comparison,
  seriesKey,
}: {
  comparison?: SnapshotComparison;
  seriesKey: KpiSeriesKey;
}) => {
  if (!comparison || comparison.delta === null || comparison.previous === null || comparison.current === null) {
    return (
      <Typography style={{ color: '#94a3b8', fontSize: 12, fontWeight: 800, marginTop: 8 }}>
        No previous snapshot
      </Typography>
    );
  }

  const delta = comparison.delta;
  const unchanged = Math.abs(delta) < getDeltaTolerance(seriesKey);
  const positive = isPositiveTrend(delta, seriesKey);
  const color = unchanged ? '#64748b' : positive ? '#15803d' : '#b91c1c';
  const background = unchanged ? '#f1f5f9' : positive ? '#dcfce7' : '#fee2e2';
  const arrow = unchanged ? '→' : delta > 0 ? '↗' : '↘';

  return (
    <Tooltip
      title={`Previous snapshot: ${comparison.previousDate ? formatDate(comparison.previousDate) : '-'} | Current snapshot: ${comparison.currentDate ? formatDate(comparison.currentDate) : '-'}`}
    >
      <Box
        display="inline-flex"
        alignItems="center"
        style={{
          gap: 6,
          marginTop: 9,
          padding: '5px 9px',
          borderRadius: 999,
          background,
          color,
          fontSize: 12,
          fontWeight: 950,
          maxWidth: '100%',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 13 }}>{arrow}</span>
        <span>{unchanged ? 'No change' : formatTrendDelta(delta, seriesKey)}</span>
        <span style={{ opacity: 0.75 }}>vs previous snapshot</span>
      </Box>
    </Tooltip>
  );
};

export const KpiCard = ({
  label,
  value,
  helper,
  color,
  trend,
  title,
}: {
  label: string;
  value: string;
  helper: string;
  color: string;
  trend?: ReactNode;
  title?: string;
}) => (
  <Card style={quietCardStyle}>
    <CardContent style={{ padding: 18 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" style={{ gap: 12 }}>
        <Box style={{ minWidth: 0 }}>
          <Typography style={{ color: '#64748b', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Tooltip title={title || value}>
            <Typography
              style={{
                color: '#0f172a',
                fontWeight: 950,
                fontSize: 28,
                lineHeight: 1.1,
                marginTop: 8,
                wordBreak: 'normal',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {value}
            </Typography>
          </Tooltip>
          {trend}
          <Typography style={{ color: '#64748b', fontSize: 13, marginTop: trend ? 7 : 8 }}>{helper}</Typography>
        </Box>
        <Box style={{ width: 10, height: 58, borderRadius: 99, background: color, flex: '0 0 auto' }} />
      </Box>
    </CardContent>
  </Card>
);

export const CoverageCell = ({ value }: { value: number | null }) => {
  const safeValue = Math.max(0, Math.min(100, value || 0));
  return (
    <Box minWidth={140} maxWidth={170}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography style={{ color: coverageColor(value), fontWeight: 950, fontSize: 13 }}>
          {value !== null ? `${value.toFixed(1)}%` : '-'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={safeValue}
        style={{ height: 7, borderRadius: 99, backgroundColor: '#e2e8f0' }}
      />
    </Box>
  );
};

export const QualityRatingBadge = ({
  label,
  value,
}: {
  label: string;
  value: number | string | null;
}) => {
  const safeValue = toNumber(value);
  const grade = ratingLabel(safeValue);

  return (
    <Tooltip title={`${label}: ${grade}${safeValue !== null ? ` (${safeValue.toFixed(2)})` : ''}`}>
      <Box
        component="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        style={{
          minWidth: 24,
          height: 22,
          padding: '0 8px',
          borderRadius: 999,
          fontWeight: 950,
          fontSize: 12,
          background: `${ratingColor(safeValue)}14`,
          color: ratingColor(safeValue),
          border: `1px solid ${ratingColor(safeValue)}44`,
        }}
      >
        {grade}
      </Box>
    </Tooltip>
  );
};

const QualityRatingItem = ({ label, value }: { label: string; value: number | null }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{
      minWidth: 0,
      gap: 8,
      padding: '4px 8px',
      borderRadius: 10,
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
    }}
  >
    <Typography
      style={{
        color: '#475569',
        fontSize: 11,
        fontWeight: 850,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <QualityRatingBadge label={label} value={value} />
  </Box>
);

export const QualityCell = ({
  reliability,
  security,
  maintainability,
}: {
  reliability: number | null;
  security: number | null;
  maintainability: number | null;
}) => (
  <Box
    display="grid"
    style={{
      gridTemplateColumns: '1fr 1fr',
      gap: 6,
      maxWidth: 285,
    }}
  >
    <QualityRatingItem label="Reliability" value={reliability} />
    <QualityRatingItem label="Security" value={security} />
    <Box style={{ gridColumn: '1 / -1' }}>
      <QualityRatingItem label="Maintainability" value={maintainability} />
    </Box>
  </Box>
);

export const TrendBadge = ({
  current,
  previous,
  metric,
}: {
  current: number | null;
  previous: number | null;
  metric: MetricKey;
}) => {
  if (current === null || previous === null) {
    return (
      <Chip
        size="small"
        label="No trend"
        style={{ background: '#f1f5f9', color: '#64748b', fontWeight: 800 }}
      />
    );
  }
  const delta = current - previous;
  const unchanged = Math.abs(delta) < getMetricDeltaTolerance(metric);
  if (unchanged) {
    return (
      <Chip
        size="small"
        label="No change"
        style={{ background: '#f1f5f9', color: '#64748b', fontWeight: 800 }}
      />
    );
  }
  const isGood = METRICS[metric].better === 'higher' ? delta >= 0 : delta <= 0;
  const prefix = delta > 0 ? '+' : '';

  return (
    <Chip
      size="small"
      label={`${prefix}${delta.toFixed(metric === 'code_smells' ? 0 : 1)}`}
      style={{
        background: isGood ? '#dcfce7' : '#fee2e2',
        color: isGood ? '#166534' : '#991b1b',
        fontWeight: 900,
      }}
    />
  );
};

export const SonarSectionHeader = ({ title }: { title: string }) => (
  <Box
    mb={2}
    mt={3}
    p={1.5}
    style={{
      textAlign: 'center',
      backgroundColor: '#1f78c1',
      color: 'white',
      fontSize: 18,
      fontWeight: 'bold',
      borderRadius: 8,
    }}
  >
    {title}
  </Box>
);